import {
	MutableRefObject,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';
import { useSelector } from 'react-redux';
import { IResultLocationFilter, SurveyResultsFilterNames } from 'models';
import {
	ICommentsAverage,
	IPointWithOffset,
	MAP_ZOOM_TYPE_INDEXES,
} from 'modules/admin-panel/pages/results/models';
import { isLocationChangedBetweenRounds } from 'helpers';
import {
	OFFICE_DEFAULT_ZOOM,
	CITY_DEFAULT_ZOOM,
	COUNTRY_DEFAULT_ZOOM,
	GLOBAL_DEFAULT_ZOOM,
} from 'modules/admin-panel/pages/results/helpers/map/resultMapConfig';
import { resetFiltersSelector } from 'modules/admin-panel/pages/results/selectors/dashboard.selectors';
import { useSurveyResultsFilter } from '../useSurveyResultsFilter';
import { useMapEffects } from './useMapEffects';
import { useMapBounding } from './useMapBounding';

interface UseMapZoomingParams {
	setLastSelected: (item: ICommentsAverage) => void;
	setActiveLocation?: (
		location: IResultLocationFilter,
		resetRegion?: boolean,
	) => void;
	markerList?: ICommentsAverage[] | null;
}

export const useMapZooming = ({
	setLastSelected,
	setActiveLocation,
	markerList,
}: UseMapZoomingParams) => {
	const { surveyFilter } = useSurveyResultsFilter();
	const filtersToReset = useSelector(resetFiltersSelector);

	const mapRef: MutableRefObject<google.maps.Map | undefined> = useRef();

	const [prevMapZoom, setPrevMapZoom] = useState<number>();

	const handleSetActiveLocation = (
		country?: string,
		city?: string,
		office?: string,
		resetRegion?: boolean,
	) => {
		setActiveLocation &&
			setActiveLocation({ country, city, office }, resetRegion);
	};

	const { panToGroupBounds, zoomToLevel, zoomSelectedOffice } = useMapBounding(
		mapRef,
		setPrevMapZoom,
	);

	const zoomToPreselectedLocation = useCallback(() => {
		if (markerList?.length) {
			const { country, city, office } = surveyFilter;
			let zoom = GLOBAL_DEFAULT_ZOOM;
			if (office) {
				zoom = OFFICE_DEFAULT_ZOOM;
			} else if (city) {
				zoom = CITY_DEFAULT_ZOOM;
			} else if (country) {
				zoom = COUNTRY_DEFAULT_ZOOM;
			}

			zoomToLevel(zoom, markerList);
		}
	}, [markerList, surveyFilter, zoomToLevel]);

	const onMapLoad = useCallback(
		(map) => {
			mapRef.current = map;
			zoomToPreselectedLocation();
		},
		[mapRef, zoomToPreselectedLocation],
	);

	const handleResetClick = () => {
		if (
			!surveyFilter[SurveyResultsFilterNames.region] &&
			!surveyFilter[SurveyResultsFilterNames.country] &&
			markerList?.length
		) {
			return zoomToLevel(GLOBAL_DEFAULT_ZOOM, markerList);
		}
		handleSetActiveLocation(undefined, undefined, undefined, true);
	};

	const changeZoomLevel = (
		selectedItem: IPointWithOffset | undefined,
		newZoomTypeIndex: number,
	) => {
		if (!mapRef.current || !selectedItem) return;
		if (newZoomTypeIndex < MAP_ZOOM_TYPE_INDEXES.worldZoom) return;
		if (newZoomTypeIndex > MAP_ZOOM_TYPE_INDEXES.officeZoom) return;

		const { country, city, office } = selectedItem;

		switch (newZoomTypeIndex) {
			case MAP_ZOOM_TYPE_INDEXES.worldZoom:
				handleSetActiveLocation();
				break;
			case MAP_ZOOM_TYPE_INDEXES.countryZoom:
				handleSetActiveLocation(country);
				break;
			case MAP_ZOOM_TYPE_INDEXES.cityZoom:
				handleSetActiveLocation(country, city);
				break;
			case MAP_ZOOM_TYPE_INDEXES.officeZoom:
				handleSetActiveLocation(country, city, office);
				break;
		}
	};

	useEffect(() => {
		if (isLocationChangedBetweenRounds(filtersToReset)) {
			zoomToPreselectedLocation();
		}
	}, [filtersToReset, zoomToPreselectedLocation]);

	useMapEffects({
		mapRef,
		markerList,
		setLastSelected,
		panToGroupBounds,
		zoomToLevel,
		zoomSelectedOffice,
	});

	return {
		mapRef,
		onMapLoad,
		changeZoomLevel,
		prevMapZoom,
		handleResetClick,
	};
};
